import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  addAAddOnToBookingContainer,
  addAccommodationToBookingContainer,
  addBooking,
  cancelBookingcall,
  cancelContainercall,
  clearContainer,
  confirmBooking,
  detachFromBookingContainer,
  fetchBookingContainerDetails,
  fetchBookings,
  fetchBookingsMessageEvents,
  fetchBookingTravelVatsDetails,
  fetchBookingVatsDetails,
  fetchInvoicePdf,
  fetchPreBookings,
  fetchSelectableAddOns,
  fetchUnConfirmedBookings,
  fetchUnConfirmedInterhomeBookings,
  fetchUnConfirmedTravelBookings,
  fetchWrongBookings,
  isChangePossible,
  makeAddOnBookingToContainer,
  makeOwnerOwnBooking,
  removeFromBookingContainer,
  sendBookingBillsMessageHandler,
  sendBookingCancellationMessageHandler,
  sendBookingConfirmationEmailHandler,
  sendBookingConfirmationEmailToMaintainerHandler,
  sendBookingConfirmationEmailToOwnerHandler,
  sendBookingPhaseMessageHandler,
  updateBookingContainer,
  updateBookingNote,
  updateBookingToPleriminary,
  updateMaintainerBookingsToNewMaintainer,
  updateOwnerBookingsToNewOwner,
} from '../../common/api/bookingsService';

import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import { makebooking } from '../../common/api/sportumService';
import { OwnerOwnBooking } from './OwnerOwnInterfaces';
import { SelectableAddOnFilter } from './Components/NewBookingModal';
import {
  InitialPaginationParameters,
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface CreateBookingNote {
  noteText: string;
}

export interface sendOwnerBookingConfirmationRequest {
  bookingId: string;
  ownerId: string;
  sendToEmail: string;
}

export interface sendMaintainerBookingConfirmationRequest {
  bookingId: string;
  maintainerId: string;
  sendToEmail: string;
}

export interface sendClientBookingConfirmationRequest {
  bookingId: string;
  sendToEmail: string;
  locale: string;
}

export enum ClientMessagePhase {
  Phase1 = 1,
  Phase2 = 2,
  Phase3 = 3,
}

export interface sendClientBillsMessageRequest {
  bookingId: string;
  sendToEmail: string;
}

export interface sendClientCancellationMessageRequest {
  bookingId: string;
  sendToEmail: string;
}

export interface sendClientPhaseMessageRequest {
  bookingId: string;
  sendToEmail: string;
  phase: ClientMessagePhase;
}

export interface CreateBookingPdf {
  invoiceId: string;
  copy: boolean;
  compensation: boolean;
  locale: string;
  advance: boolean;
}

export interface CreateBooking {
  bookingNumber: number;
  payerId: string;
  accommodationId: string;
  arrivalDateTime: string;
  departureDateTime: string;
  customerBookingNote: CreateBookingNote;
  ownerBookingNote: CreateBookingNote;
  officerBookingNote: CreateBookingNote;
}

export interface UpdateBookingToPreliminary {
  bookingId: string;
}

export interface CancelBooking {
  bookingId: string;
  reasonOfCancellation: string;
  expectedBookingType?: number;
}

export interface CancelContainer {
  containerId: string;
  reasonOfCancellation: string;
}

export interface BookingInvoiceItem {
  id: string;
  referenceNumber: number;
  discriminator: string;
  number: number;
  invoiceDate: string;
  entryDate: string;
  dueDate: string;
  booking: string;
  delayFeeP: number;
  compensationInvoiceId: string;
  originalInvoiceId: string;
  isAdvance: boolean;
  siteId: string;
  openBalance?: number;
}

export interface AddAccommodationToBookingContainerItem {
  bookingContainerId: string;
  accommodationId: string;
  arrivalDateTime: string;
  departureDateTime: string;
  adults: number;
  childs: number;
  grossPrice: number;
  netPrice: number;
  advanceBillGrossPrice: number;
  finalBillPrice: number;
  finalBillNetPrice: number;
  purchasePrice: number;
  vatPerCent: number;
  bypassRules: boolean;
}

export interface AddAddOnToBookingContainerItem {
  bookingContainerId: string;
  accommodationAddOnId: string;
  accommodationId: string;
  bookingId: string;
  arrivalDateTime: string;
  departureDateTime: string;
  adults: number;
  childs: number;
  units: number;
  grossPrice: number;
  netPrice: number;
  advanceBillGrossPrice: number;
  finalBillPrice: number;
  finalBillNetPrice: number;
  sitePurchasePrice: number;
  vatPerCent: number;
}

export interface RemoveFromBookingContainerItem {
  bookingContainerId: string;
  bookingId: string;
  orderItemId: string;
  removedAddOns: [];
}

export interface DetachFromBookingContainerItem {
  bookingContainerId: string;
  bookingId: string;
}

export interface ClearContainerItem {
  bookingContainerId: string;
}

export interface BookingDetailsNoteItem {
  id?: string;
  noteText: string;
}
export interface PurchasePriceItem {
  sitePurchasePrice: number;
  ownerPurchasePrice: number;
  sitePurchasePriceVat: number;
  ownerPurchasePriceVat: number;
}
export interface PricesItem {
  grossPrice: number;
  grossPriceVat: number;
  finalBillNetPrice: number;
  netPrice: number;
  finalBillPrice: number;
  discount: number;
  discountP: number;
  comission: number;
  comissionP: number;
  travelAgencyComission: number;
  travelAgencyComissionP: number;
  travelAgencyOnlyComission: number;
  sellerCommission: number;
  finalBillPriceWithoutVat: number;
  finalBillPriceVat: number;
  finalBillNetPriceWithoutVat: number;
  finalBillNetPriceVat: number;
  advanceBillPrice: number;
  advanceBillPriceVat: number;
  advanceBillPriceWithoutVat: number;
  vatP: number;
  travelAgencyVatP: number;
}

export interface OptiwattiData {
  accommodationBookingOrderItemId: string;
  eventGuid: string;
  status: string;
  message: string;
  translatedStatus: string;
}
export interface CareTakerItem {
  id: string;
  ownerNameNumber?: string;
  email?: string;
}

interface RoleEmailDto {
  isPrimary: boolean;
  isBookingNoteEmail: boolean;
  description?: string;
  emailAddress?: string;
}

// Define a mapping object for roleEnum translations
const ROLE_TRANSLATIONS: Record<string, string> = {
  OWNER_ROLE_TYPE: 'Omistaja',
  CARETAKER_ROLE_TYPE: 'Huoltaja',
  CARETAKER_CLEANER_TYPE: 'Siivooja',
  CARETAKER_LINEN_TYPE: 'Liinavaatehuolto',
  CARETAKER_KEY_TYPE: 'Avainhuolto',
  CARETAKER_SPARE_TYPE: 'Varahuoltaja',
};
// Function to get the translated role name
export const translateRoleEnum = (roleEnum: string): string => {
  return ROLE_TRANSLATIONS[roleEnum] || 'Tuntematon rooli'; // Fallback text
};

export interface BookingNoteSettings {
  bookingNoteByEmail: boolean;
  bookingNoteByMail: boolean;
  bookingNoteBySms: boolean;
  bookingNoteImmediatelly: boolean;
  bookingNoteWeekly: boolean;
  noMassSms: boolean;
  noBookingNote: boolean;
}

export interface AccommodationAccountRoleDto {
  id: string;
  accountId: string;
  roleFirstName?: string;
  roleLastName?: string;
  roleId: string;
  roleEnum: string;
  emails: RoleEmailDto[];
  bookingNoteSettings: BookingNoteSettings;
}

export interface BookingDetailsOrderItem {
  id?: string;
  isCancelled: boolean;
  orderItemType: number;
  accommodationId: string;
  addOnId: string;
  accommodationNumber: number;
  addOnNumber?: number;
  arrivalDateTime: string;
  departureDateTime: string;
  startDateTime: string;
  endDateTime: string;
  itemName: string;
  optiwattiData?: OptiwattiData;
  pricesResponse: PricesItem;
  purchasePricesResponse: PurchasePriceItem;
  includedInPrice: boolean;
  amountOfAdults: number;
  amountOfChildren: number;
  units: number;
  amountPerQuantity?: boolean;
  durationPerNight?: boolean;
  amountSingeCharge?: boolean;
  amountPerGuest?: boolean;
  ownerAccountId?: string;
  realOwnerAccountId?: string;
  ownerName?: string;
  realOwnerName?: string;
  careTakers: CareTakerItem[];
  ownerEmail?: string;
  ownerBookingNoteEmail?: string;
  realOwnerEmail?: string;
  realOwnerBookingNoteEmail?: string;
  maintainerAccountId?: string;
  maintainerName?: string;
  maintainerEmail?: string;
  accommodationAccountRoles: AccommodationAccountRoleDto[];
}
export interface BookingVatItem {
  vatP: number;
  advanceNetto: number;
  finalNetto: number;
  advanceVatAmount: number;
  finalVatAmount: number;
  advanceTotal: number;
  finalTotal: number;
}
interface OwnerVatItem {
  vatP: number;
  netto: number;
  vatAmount: number;
  total: number;
  itemNameNumber: string | null;
}
interface OwnerVat {
  ownerNameNumber: string;
  ownerPhone: string;
  ownerEmail: string;
  caretakerNameNumber: string | null;
  caretakerPhone: string | null;
  caretakerEmail: string | null;
  ownerVatItems: OwnerVatItem[];
}
export interface BookingVats {
  vatItems: BookingVatItem[];
  agentVatItems: BookingVatItem[];
  ownerVats: OwnerVat[];
  careTakerVats: OwnerVat[];
}
export interface WebShopCustomerItem {
  customerNumber: string;
  firstName: string;
  lastName: string;
  streetAddress: string;
  additionalAddress: string;
  postalCode: string;
  postCity: string;
  countryId: string;
  phoneNumber: string;
  email: string;
  mobileNumber: string;
  companyName: string;
}
export interface ExtraBookingDetailsData {
  id: string;
  accommodationReclamationText: string;
  createdAt: string;
  payerId: string;
  authorId: string;
  bookingType: number;
  bookingNumber: number;
  payerFirstName: string;
  payerLastName: string;
  payerAccountNumber: string;
  accommodationId: string;
  isCancelled: boolean;
  bookingOrderItems: BookingDetailsOrderItem[];
  customerBookingNotes: BookingDetailsNoteItem[];
  ownerBookingNotes: BookingDetailsNoteItem[];
  officerBookingNotes: BookingDetailsNoteItem[];
  finalBillPrice: number;
  finalBillPriceWithoutVat: number;
  finalBillPriceVat: number;
  advanceBillPrice: number;
  advanceBillPriceVat: number;
  advanceBillPriceWithoutVat: number;
  bookingContainerId: string;
  billingLocale: number;
  bookingInvoiceItems: BookingInvoiceItem[];
  preliminaryBookingExpirationDate: string;
  errors: string[];
  webShopCustomer: WebShopCustomerItem;
  isAllowedToCancel: boolean;
  isAllowedToEdit: boolean;
  isAddOnBooking: boolean;
  preBookingDueDate?: string;
}

export interface TravelAgencyData {
  finalClientFirstName: string;
  finalClientLastName: string;
  finalClientCompany?: string;
  finalClientStreetAddress: string;
  finalClientZipCode: string;
  finalClientCity: string;
  finalClientCountry: string;
  finalClientEmail: string;
  finalClientPhone: string;
}

export interface TravelAgencyAccountData {
  finalClientFirstName: string;
  finalClientLastName: string;
  finalClientCompany?: string;
  finalClientStreetAddress: string;
  finalClientZipCode: string;
  finalClientCity: string;
  finalClientCountry: string;
  finalClientEmail: string;
  finalClientPhone: string;
  travelAgencyPhone: string;
  travelAgencyEmail: string;
}

export interface ExternalBookingIdsDto {
  externalId: string;
  bookingIdsType: number;
  isDeleted: boolean;
  externalRestApiBookingId: string;
  externalBookingSource: string;
}
export interface BookingDetailsData {
  id: string;
  keyBox: string;
  accommodationReclamationText: string;
  AccommodationSiteIsCurrentSite: boolean;
  crossSiteBooking: boolean;
  createdAt: string;
  updatedAt: string;
  cancelledAt: string;
  payerId: string;
  authorId: string;
  bookingType: number;
  bookingNumber: number;
  payerFirstName: string;
  payerLastName: string;
  payerAccountNumber: string;
  payerStreetAddress: string;
  payerPostCode: string;
  payerPostCity: string;
  payerPhoneNumber: string;
  payerEmailAddress: string;
  accommodationId: string;
  isCancelled: boolean;
  bookingOrderItems: BookingDetailsOrderItem[];
  customerBookingNotes: BookingDetailsNoteItem[];
  ownerBookingNotes: BookingDetailsNoteItem[];
  officerBookingNotes: BookingDetailsNoteItem[];
  netPrice: number;
  travelAgencyNetPrice: number;
  finalBillPrice: number;
  finalBillPriceWithoutVat: number;
  finalBillPriceVat: number;
  advanceBillPrice: number;
  overrideAdvanceBillPrice: number;
  overrideTravelCommission?: number;
  advanceBillPriceVat: number;
  advanceBillPriceWithoutVat: number;
  bookingContainerId: string;
  invoicingLanguageLocale: string;
  bookingInvoiceItems: BookingInvoiceItem[];
  errors: string[];
  webShopCustomer: WebShopCustomerItem;
  isAllowedToCancel: boolean;
  isAllowedToEdit: boolean;
  isAddOnBooking: boolean;
  reasonOfCancellation: string;
  cancellerLastName: string;
  cancellerFirstName: string;
  authorFirstName: string;
  authorLastName: string;
  authorEmail: string;
  extraBookingResponses: ExtraBookingDetailsData[];
  travelAgencyData: TravelAgencyData;
  travelAgencyAccountData: TravelAgencyAccountData;
  externalBookingIdsDto: ExternalBookingIdsDto;
  paymentAmount: number;
  advanceBilled: boolean;
  paymentDate: string;
  paymentMethod: string;
  onlinePaymentCompleted: boolean;
  preliminaryBookingDueDate?: string;
  byPassAdvanceSum: boolean;
  byPassSellPriceCheck: boolean;
  removedAddOns: [];
  ByPassAdvanceSum;
  advanceInvoiceDueDate?: string;
  finalInvoiceDueDate?: string;
  officeFeeRemoved: false;
  sellerSiteId?: string;
  invoiceVisiblePrice: number;
  ownSiteAccommodation: boolean;
  canViewCustomer: boolean;
}

export interface BookingContainer {
  id: string;
  bookingResponses: BookingDetailsData[];
  advancePaid: boolean;
  acceptMarketing: boolean;
  acceptCancelInsuranceTerms: boolean;
  acceptBookingTerms: boolean;
  siteId: string;
  adults: number;
  childs: number;
  bookingContainerStatus: number;
  containerTotal: number;
  containerTravelTotal: number;
}
export interface BookingContainerUpdateItem {
  bookingContainerId: string;
  bookings: BookingDetailsData[];
  advancePaid: boolean;
  acceptMarketing: boolean;
  acceptCancelInsuranceTerms: boolean;
  officeFeeRemoved: boolean;
  acceptBookingTerms: boolean;
  adults: number;
  childs: number;
  bookingContainerStatus: number;
  finalSave?: boolean;
}

export interface BookingListItemAccommodationData {
  accommodationName: string;
  accommodationNumber: number;
}
export interface BookingListItem {
  id: string;
  startDate: string;
  endDate: string;
  bookingNumber: number;
  groupedBookingNumber: string;
  bookingType: string;
  payerFirstName: string;
  payerLastName: string;
  authorEmail: string;
  isCancelled: boolean;
  isEdited: boolean;
  isConfirmed: boolean;
  cancellerEmail: string;
  officerLastName: string;
  createdAt: string;
  updatedAt: string;
  onlinePaymentCompleted: boolean;
  paymentMethod: string;
  accommodationResponse: BookingListItemAccommodationData;
  isDueDateGone: boolean;
  preliminaryBookingDueDate: string;
  restApiBookingId?: string;
  additionalInformation?: string;
  finalClient?: string;
  travelAgency?: string;
}
export interface BookingChangePossibleRequest {
  accommodationId: string;
  startDate: string;
  endDate: string;
  existingBookingId: string;
}
export interface UpdateBookingNoteItem {
  bookingId: string;
  notesType: number;
  bookingNote: {
    noteText: string;
    id: string;
  };
}
export interface BookingsFilter extends PaginationParameters {
  searchString: string;
  authorId: string;
  bookingTypes: string[];
  isCancelled: boolean | null;
  bookingStartDate?: string;
  bookingEndDate?: string;
  bookingStartsFrom?: string;
  bookingStartsTo?: string;
  bookingStartsWithinDays?: number;
  externalBookingsOnly?: boolean;
  externalBookingSources: string[];
  createdFrom?: string;
  createdTo?: string;
  searchInBooking: boolean;
  searchInCustomer: boolean;

  searchInNotes: boolean;
  searchInAccommodation: boolean;
  searchInBilling: boolean;
  searchInAddon: boolean;
  bookingAuthorName?: string;
  bookingCancellerName?: string;
  bookingMainType?: 'accommodation' | 'addon';
  onlyAccommodationBookings?: boolean;
  onlyAddOnBookings?: boolean;
}

export interface BookingMessageEvent {
  id: string;
  bookingId: string;
  bookingMessageEventType: number;
  emailAddresses: string[];
  phoneNumbers: string[];
  smsMessageText: string;
  sentTime: string;
  transactionalMessageDtoJson: string;
}

export interface BookingsState {
  bookingItems: BookingListItem[];
  preBookingItems: BookingListItem[];
  unConfirmedBookingItems: BookingListItem[];
  unConfirmedInterhomeBookingItems: BookingListItem[];
  wrongBookingItems: BookingListItem[];
  unConfirmedTravelBookingItems: BookingListItem[];
  selectableAddOns: BookingDetailsOrderItem[];
  bookingContainer: BookingContainer;
  basketBookingContainer: BookingContainer;
  bookingVatReport: BookingVats;
  bookingTravelVatReport: BookingVats;
  bookingMessageEvents: BookingMessageEvent[];
  bookingFilter: BookingsFilter;
  pagination: PaginationResponse;
  webShopCustomerState: WebShopCustomerItem;
  bookingTravelvatsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingvatsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  selectableAddOnsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  preBookingItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  unConfirmedBookingItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  unConfirmedInterhomeBookingItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  wrongBookingItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  unConfirmedTravelBookingItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingMessageEventsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  isBookingChangePossibleStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingChangePossible: boolean;
}

const initialState: BookingsState = {
  preBookingItemsStatus: LOADING_STATUS.IDLE,
  unConfirmedBookingItemsStatus: LOADING_STATUS.IDLE,
  unConfirmedBookingItems: [],
  unConfirmedInterhomeBookingItems: [],
  wrongBookingItems: [],
  unConfirmedTravelBookingItems: [],
  unConfirmedTravelBookingItemsStatus: LOADING_STATUS.IDLE,
  unConfirmedInterhomeBookingItemsStatus: LOADING_STATUS.IDLE,
  wrongBookingItemsStatus: LOADING_STATUS.IDLE,
  bookingMessageEvents: [],
  preBookingItems: [],
  bookingItems: [],
  bookingVatReport: {
    vatItems: [],
  } as BookingVats,
  bookingTravelVatReport: {
    vatItems: [],
    agentVatItems: [],
  } as BookingVats,
  bookingvatsStatus: LOADING_STATUS.IDLE,
  bookingTravelvatsStatus: LOADING_STATUS.IDLE,
  selectableAddOns: [],
  selectableAddOnsStatus: LOADING_STATUS.IDLE,
  bookingContainer: {
    id: '',
    acceptBookingTerms: false,
    adults: 0,
    siteId: '',
    acceptMarketing: false,
    advancePaid: false,
    childs: 0,
    acceptCancelInsuranceTerms: false,
    bookingResponses: [],
    bookingContainerStatus: 0,
    containerTotal: 0,
    containerTravelTotal: 0,
  },
  basketBookingContainer: {
    id: '',
    acceptBookingTerms: false,
    adults: 0,
    siteId: '',
    acceptMarketing: false,
    advancePaid: false,
    childs: 0,
    acceptCancelInsuranceTerms: false,
    bookingResponses: [],
    bookingContainerStatus: 0,
    containerTotal: 0,
    containerTravelTotal: 0,
  },
  bookingFilter: {
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: 50,
    searchString: '',
    authorId: '',
    bookingTypes: [],
    isCancelled: null,
    externalBookingsOnly: undefined,
    externalBookingSources: [],
    searchInBooking: false,
    searchInCustomer: false,
    searchInNotes: false,
    searchInAccommodation: false,
    searchInBilling: false,
    searchInAddon: false,
    bookingMainType: undefined,
  },
  webShopCustomerState: {
    lastName: '',
    additionalAddress: '',
    companyName: '',
    customerNumber: '',
    email: '',
    countryId: '',
    firstName: '',
    streetAddress: '',
    mobileNumber: '',
    phoneNumber: '',
    postalCode: '',
    postCity: '',
  },
  pagination: InitialPaginationResponse,
  bookingItemsStatus: LOADING_STATUS.IDLE,
  bookingDetailsStatus: LOADING_STATUS.IDLE,
  bookingMessageEventsStatus: LOADING_STATUS.IDLE,
  isBookingChangePossibleStatus: LOADING_STATUS.IDLE,
  bookingChangePossible: false,
};

// Add these thunk actions after your existing thunk declarations
export const getWebShopCustomerState = createAsyncThunk(
  'bookings/getWebShopCustomerState',
  async (webShopCustomer: WebShopCustomerItem) => {
    return webShopCustomer;
  },
);

export const setWebShopCustomerState = createAsyncThunk(
  'bookings/setWebShopCustomerState',
  async (webShopCustomer: WebShopCustomerItem) => {
    return webShopCustomer;
  },
);

export const setBookingContainer = createAsyncThunk(
  'bookings/setBookingContainer',
  async (bookingContainer: BookingContainer) => {
    return bookingContainer;
  },
);

export const setBasketContainer = createAsyncThunk(
  'bookings/setBasketContainer',
  async (basketBookingContainer: BookingContainer) => {
    return basketBookingContainer;
  },
);

export const getBookingsAsync = createAsyncThunk(
  'bookings/getBookings',
  async (bookingsFilter: BookingsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchBookings(bookingsFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const getIsBookingChangePossible = createAsyncThunk(
  'accommodations/getIsBookingChangePossible',
  async (changeData: BookingChangePossibleRequest, { rejectWithValue }) => {
    try {
      const response = await isChangePossible(changeData);
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.response.data);
    }
  },
);
export const getBookingsMessageEventsAsync = createAsyncThunk(
  'bookings/getBookingsMessageEvents',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await fetchBookingsMessageEvents(bookingId);
      return response.result.bookingMessageEvents;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveOwnerOwnBookingAsync = createAsyncThunk(
  'bookings/saveOwnerOwnBooking',
  async (ownerOwnBooking: OwnerOwnBooking, { rejectWithValue }) => {
    try {
      const response = await makeOwnerOwnBooking(ownerOwnBooking);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const getPreBookingsAsync = createAsyncThunk(
  'bookings/getPreBookings',
  async (bookingsFilter: BookingsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchPreBookings(bookingsFilter);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getUnConfirmedBookingsAsync = createAsyncThunk(
  'bookings/getUnConfirmedBookings',
  async (bookingsFilter: BookingsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchUnConfirmedBookings(bookingsFilter);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getUnConfirmedInterhomeBookingsAsync = createAsyncThunk(
  'bookings/getUnConfirmedInterhomeBookings',
  async (bookingsFilter: BookingsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchUnConfirmedInterhomeBookings(bookingsFilter);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getWrongBookingsAsync = createAsyncThunk(
  'bookings/getWrongBookingsAsync',
  async (bookingsFilter: BookingsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchWrongBookings(bookingsFilter);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getUnConfirmedTravelBookingsAsync = createAsyncThunk(
  'bookings/getUnConfirmedTravelBookings',
  async (bookingsFilter: BookingsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchUnConfirmedTravelBookings(bookingsFilter);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const moveOwnerBookingsToNewOwnerAsync = createAsyncThunk(
  'bookings/moveOwnerBookingsToNewOwnerAsync',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await updateOwnerBookingsToNewOwner(accommodationId);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const moveMaintainerBookingsToNewMaintainerAsync = createAsyncThunk(
  'bookings/moveMaintainerBookingsToNewMaintainerAsync',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response =
        await updateMaintainerBookingsToNewMaintainer(accommodationId);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const getBookingContainerDetailsAsync = createAsyncThunk(
  'bookings/getBookingContainerDetailsAsync',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await fetchBookingContainerDetails(bookingId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const getInvoiceDetailsPdfAsync = createAsyncThunk(
  'bookings/getInvoiceDetailsPdfAsync',
  async (invoiceId: CreateBookingPdf, { rejectWithValue }) => {
    try {
      const response = await fetchInvoicePdf(invoiceId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendBookingConfirmationEmail = createAsyncThunk(
  'bookings/sendBookingConfirmationEmail',
  async (
    sendClientBookingConfirmationRequest: sendClientBookingConfirmationRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await sendBookingConfirmationEmailHandler(
        sendClientBookingConfirmationRequest.bookingId,
        sendClientBookingConfirmationRequest.sendToEmail,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendBookingCancellationMessageEmail = createAsyncThunk(
  'bookings/sendBookingCancellationMessageEmail',
  async (
    sendClientPhaseMessageRequest: sendClientCancellationMessageRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await sendBookingCancellationMessageHandler(
        sendClientPhaseMessageRequest.bookingId,
        sendClientPhaseMessageRequest.sendToEmail,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendBookingPhaseMessageEmail = createAsyncThunk(
  'bookings/sendBookingPhaseMessageEmail',
  async (
    sendClientPhaseMessageRequest: sendClientPhaseMessageRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await sendBookingPhaseMessageHandler(
        sendClientPhaseMessageRequest.bookingId,
        sendClientPhaseMessageRequest.sendToEmail,
        sendClientPhaseMessageRequest.phase,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendBookingBillsMessageEmail = createAsyncThunk(
  'bookings/sendBookingBillsMessageEmail',
  async (
    sendClientBillsMessageRequest: sendClientBillsMessageRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await sendBookingBillsMessageHandler(
        sendClientBillsMessageRequest.bookingId,
        sendClientBillsMessageRequest.sendToEmail,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendBookingConfirmationEmailToOwner = createAsyncThunk(
  'bookings/sendBookingConfirmationEmailToOwner',
  async (
    sendOwnerBookingConfirmationRequest: sendOwnerBookingConfirmationRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await sendBookingConfirmationEmailToOwnerHandler(
        sendOwnerBookingConfirmationRequest.bookingId,
        sendOwnerBookingConfirmationRequest.ownerId,
        sendOwnerBookingConfirmationRequest.sendToEmail,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const sendBookingConfirmationEmailToMaintainer = createAsyncThunk(
  'bookings/sendBookingConfirmationEmailToMaintainer',
  async (
    sendMaintainerBookingConfirmationRequest: sendMaintainerBookingConfirmationRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await sendBookingConfirmationEmailToMaintainerHandler(
        sendMaintainerBookingConfirmationRequest.bookingId,
        sendMaintainerBookingConfirmationRequest.maintainerId,
        sendMaintainerBookingConfirmationRequest.sendToEmail,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getBookingvatsAsync = createAsyncThunk(
  'bookings/getBookingvatsAsync',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await fetchBookingVatsDetails(bookingId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getBookingTravelvatsAsync = createAsyncThunk(
  'bookings/getBookingTravelvatsAsync',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await fetchBookingTravelVatsDetails(bookingId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSelectableAddOnsAsync = createAsyncThunk(
  'bookings/getSelectableAddOnsAsync',
  async (filter: SelectableAddOnFilter, { rejectWithValue }) => {
    try {
      const response = await fetchSelectableAddOns(
        filter.accommodationId,
        filter.units,
        filter.adults,
        filter.childs,
        filter.includeNonVisible,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveBookingAsync = createAsyncThunk(
  'bookings/addBooking',
  async (bookingData: CreateBooking, { rejectWithValue }) => {
    try {
      const response = await addBooking(bookingData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const cancelBookingAsync = createAsyncThunk(
  'bookings/cancelBooking',
  async (cancelBookingCommand: CancelBooking, { rejectWithValue }) => {
    try {
      const response = await cancelBookingcall(
        cancelBookingCommand.bookingId,
        cancelBookingCommand.reasonOfCancellation,
        cancelBookingCommand.expectedBookingType,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const cancelContainerAsync = createAsyncThunk(
  'bookings/cancelContainer',
  async (cancelContainerCommand: CancelContainer, { rejectWithValue }) => {
    try {
      const response = await cancelContainercall(
        cancelContainerCommand.containerId,
        cancelContainerCommand.reasonOfCancellation,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAccommodationToBookingContainerAsync = createAsyncThunk(
  'bookings/addAccommodationToBookingContainer',
  async (
    accommodationData: AddAccommodationToBookingContainerItem,
    { rejectWithValue },
  ) => {
    try {
      const response =
        await addAccommodationToBookingContainer(accommodationData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAddOnBookingToBookingContainerAsync = createAsyncThunk(
  'bookings/addAccommodationToBookingContainer',
  async (
    accommodationData: AddAddOnToBookingContainerItem,
    { rejectWithValue },
  ) => {
    try {
      const response = await makeAddOnBookingToContainer(accommodationData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateBookingContainerAsync = createAsyncThunk(
  'bookings/updateBookingContainerAsync',
  async (
    accommodationData: BookingContainerUpdateItem,
    { rejectWithValue },
  ) => {
    try {
      // console.debug(
      //   'pyydetty ennakkomaksun määrä override:',
      //   accommodationData.bookings[0].overrideAdvanceBillPrice,
      // );
      // console.debug(
      //   'ennakkomaksun määrä varauksella:',
      //   accommodationData.bookings[0].advanceBillPrice,
      // );
      const response = await updateBookingContainer(accommodationData);
      // console.debug(
      //   'paluusanoman ennakkomaksun määrä override:',
      //   response.result.bookingResponses[0].overrideAdvanceBillPrice,
      // );
      // console.debug(
      //   'paluusanoman ennakkomaksun määrä:',
      //   response.result.bookingResponses[0].advanceBillPrice,
      // );
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const updateBookingNoteAsync = createAsyncThunk(
  'bookings/updateBookingNoteAsync',
  async (accommodationData: UpdateBookingNoteItem, { rejectWithValue }) => {
    try {
      const response = await updateBookingNote(accommodationData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const confirmBookingAsync = createAsyncThunk(
  'bookings/confirmBooking',
  async (accommodationData: BookingDetailsData, { rejectWithValue }) => {
    try {
      const response = await confirmBooking(accommodationData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAddOnToBookingContainerAsync = createAsyncThunk(
  'bookings/addAddOnToBookingContainer',
  async (addOnData: AddAddOnToBookingContainerItem, { rejectWithValue }) => {
    try {
      const response = await addAAddOnToBookingContainer(addOnData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const removeFromBookingContainerAsync = createAsyncThunk(
  'bookings/removeFromBookingContainer',
  async (
    removeDataData: RemoveFromBookingContainerItem,
    { rejectWithValue },
  ) => {
    try {
      const response = await removeFromBookingContainer(removeDataData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const detachFromBookingContainerAsync = createAsyncThunk(
  'bookings/detachFromBookingContainerAsync',
  async (
    removeDataData: DetachFromBookingContainerItem,
    { rejectWithValue },
  ) => {
    try {
      const response = await detachFromBookingContainer(removeDataData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const ClearContainerAsync = createAsyncThunk(
  'bookings/ClearContainer',
  async (removeDataData: ClearContainerItem, { rejectWithValue }) => {
    try {
      const response = await clearContainer(removeDataData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const UpdateBookingToPreliminaryAsync = createAsyncThunk(
  'bookings/UpdateBookingToPreliminaryAsync',
  async (updateDataData: UpdateBookingToPreliminary, { rejectWithValue }) => {
    try {
      const response = await updateBookingToPleriminary(updateDataData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const makeSportumBookingAsync = createAsyncThunk(
  'integrations/sportum/MakeBooking',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await makebooking(bookingId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    setSearchFilters(state, action: PayloadAction<BookingsFilter>) {
      state.bookingFilter = action.payload;
    },
    resetFilters(state) {
      state.bookingFilter = initialState.bookingFilter;
      state.bookingItems = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getBookingvatsAsync.pending, state => {
        state.bookingvatsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingvatsAsync.fulfilled, (state, action) => {
        state.bookingvatsStatus = LOADING_STATUS.IDLE;
        state.bookingVatReport = action.payload;
      })
      .addCase(getBookingvatsAsync.rejected, state => {
        state.bookingvatsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingTravelvatsAsync.pending, state => {
        state.bookingTravelvatsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingTravelvatsAsync.fulfilled, (state, action) => {
        state.bookingTravelvatsStatus = LOADING_STATUS.IDLE;
        state.bookingTravelVatReport = action.payload;
      })
      .addCase(getBookingTravelvatsAsync.rejected, state => {
        state.bookingTravelvatsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingContainerDetailsAsync.pending, state => {
        state.bookingDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingContainerDetailsAsync.fulfilled, (state, action) => {
        state.bookingDetailsStatus = LOADING_STATUS.IDLE;
        state.bookingContainer = action.payload;
      })
      .addCase(getBookingContainerDetailsAsync.rejected, state => {
        state.bookingDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingsAsync.pending, state => {
        state.bookingItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingsAsync.fulfilled, (state, action) => {
        state.bookingItemsStatus = LOADING_STATUS.IDLE;
        state.bookingItems = action.payload.data;
        state.pagination = PopulatePagination(action.payload);
      })
      .addCase(getBookingsAsync.rejected, state => {
        state.bookingItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getPreBookingsAsync.pending, state => {
        state.preBookingItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getPreBookingsAsync.fulfilled, (state, action) => {
        state.preBookingItemsStatus = LOADING_STATUS.IDLE;
        state.preBookingItems = action.payload;
      })
      .addCase(getPreBookingsAsync.rejected, state => {
        state.preBookingItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getUnConfirmedBookingsAsync.pending, state => {
        state.unConfirmedBookingItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getUnConfirmedBookingsAsync.fulfilled, (state, action) => {
        state.unConfirmedBookingItemsStatus = LOADING_STATUS.IDLE;
        state.unConfirmedBookingItems = action.payload;
      })
      .addCase(getUnConfirmedBookingsAsync.rejected, state => {
        state.unConfirmedBookingItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getUnConfirmedInterhomeBookingsAsync.pending, state => {
        state.unConfirmedInterhomeBookingItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getUnConfirmedInterhomeBookingsAsync.fulfilled,
        (state, action) => {
          state.unConfirmedInterhomeBookingItemsStatus = LOADING_STATUS.IDLE;
          state.unConfirmedInterhomeBookingItems = action.payload;
        },
      )
      .addCase(getUnConfirmedInterhomeBookingsAsync.rejected, state => {
        state.unConfirmedInterhomeBookingItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getWrongBookingsAsync.pending, state => {
        state.wrongBookingItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getWrongBookingsAsync.fulfilled, (state, action) => {
        state.wrongBookingItemsStatus = LOADING_STATUS.IDLE;
        state.wrongBookingItems = action.payload;
      })
      .addCase(getWrongBookingsAsync.rejected, state => {
        state.wrongBookingItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getUnConfirmedTravelBookingsAsync.pending, state => {
        state.unConfirmedTravelBookingItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getUnConfirmedTravelBookingsAsync.fulfilled, (state, action) => {
        state.unConfirmedTravelBookingItemsStatus = LOADING_STATUS.IDLE;
        state.unConfirmedTravelBookingItems = action.payload;
      })
      .addCase(getUnConfirmedTravelBookingsAsync.rejected, state => {
        state.unConfirmedTravelBookingItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(cancelBookingAsync.fulfilled, (state, action) => {
        if (
          state != null &&
          state.bookingContainer != null &&
          state.bookingContainer.bookingResponses != null &&
          state.bookingContainer.bookingResponses.length > 0
        ) {
          state.bookingContainer.bookingResponses[0].isCancelled =
            action.payload;
        }
      })
      .addCase(setBookingContainer.fulfilled, (state, action) => {
        state.bookingContainer = action.payload;
      })
      .addCase(setWebShopCustomerState.fulfilled, (state, action) => {
        state.webShopCustomerState = action.payload;
      })
      .addCase(setBasketContainer.fulfilled, (state, action) => {
        state.basketBookingContainer = action.payload;
      })
      .addCase(getSelectableAddOnsAsync.fulfilled, (state, action) => {
        state.selectableAddOns = action.payload;
        state.selectableAddOnsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getSelectableAddOnsAsync.rejected, state => {
        state.selectableAddOnsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getSelectableAddOnsAsync.pending, state => {
        state.selectableAddOnsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingsMessageEventsAsync.fulfilled, (state, action) => {
        state.bookingMessageEvents = action.payload;
        state.bookingMessageEventsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getBookingsMessageEventsAsync.rejected, state => {
        state.bookingMessageEventsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingsMessageEventsAsync.pending, state => {
        state.bookingMessageEventsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getIsBookingChangePossible.pending, state => {
        state.isBookingChangePossibleStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getIsBookingChangePossible.fulfilled, (state, action) => {
        state.isBookingChangePossibleStatus = LOADING_STATUS.IDLE;
        console.log(action.payload.bookingChangePossible);
        state.bookingChangePossible = action.payload.bookingChangePossible;
      })
      .addCase(getIsBookingChangePossible.rejected, state => {
        state.isBookingChangePossibleStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectBookingDetails = (state: RootState) =>
  state.bookings.bookingContainer;

export const selectWebShopCustomerState = (state: RootState) =>
  state.bookings.webShopCustomerState;

export const selectBasketBookingContainer = (state: RootState) =>
  state.bookings.basketBookingContainer;

export const selectBookingContainer = (state: RootState) =>
  state.bookings.bookingContainer;

export const selectBookingDetailsLoadingStatus = (state: RootState) =>
  state.bookings.bookingDetailsStatus;

export const selectWrongBookingStatus = (state: RootState) =>
  state.bookings.wrongBookingItemsStatus;

export const selectBookingItems = (state: RootState) =>
  state.bookings.bookingItems;

export const selectBookingsSearchFilter = (state: RootState) =>
  state.bookings.bookingFilter;

export const selectBookingsPagination = (state: RootState) =>
  state.bookings.pagination;

export const selectBookingVatReport = (state: RootState) =>
  state.bookings.bookingVatReport;

export const selectBookingTravelVatReport = (state: RootState) =>
  state.bookings.bookingTravelVatReport;

export const selectPreBookingItems = (state: RootState) =>
  state.bookings.preBookingItems;

export const selectUnConfirmedBookingItems = (state: RootState) =>
  state.bookings.unConfirmedBookingItems;

export const selectUnConfirmedInterhomeBookingItems = (state: RootState) =>
  state.bookings.unConfirmedInterhomeBookingItems;

export const selectWrongBookingItems = (state: RootState) =>
  state.bookings.wrongBookingItems;

export const selectUnConfirmedTravelBookingItems = (state: RootState) =>
  state.bookings.unConfirmedTravelBookingItems;
export const selectselectableAddOns = (state: RootState) =>
  state.bookings.selectableAddOns;
export const selectBookingItemsLoadingStatus = (state: RootState) =>
  state.bookings.bookingItemsStatus;

export const selectSelectableAddOnsStatus = (state: RootState) =>
  state.bookings.selectableAddOnsStatus;

export const selectBookingMessageEventsStatus = (state: RootState) =>
  state.bookings.bookingMessageEventsStatus;

export const selectBookingMessageEvents = (state: RootState) =>
  state.bookings.bookingMessageEvents;

export const { setSearchFilters, resetFilters } = bookingsSlice.actions;

export default bookingsSlice.reducer;
